import { useQuery } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'
import { Scale, CheckCircle2, AlertTriangle, ShieldCheck, ChevronRight } from 'lucide-react'
import { reconciliationService } from '@/services/reconciliation'
import type { GoldenReconRow } from '@/types'

function screenBadge(outcome?: string) {
  const map: Record<string, string> = {
    PASS: 'text-emerald-400 border-emerald-400/30 bg-emerald-400/[0.06]',
    EXCEPTION: 'text-amber-400 border-amber-400/30 bg-amber-400/[0.06]',
    INCOMPLETE: 'text-red-400 border-red-400/30 bg-red-400/[0.06]',
  }
  return map[outcome ?? ''] ?? 'text-stone-400 border-stone-500/30 bg-stone-500/[0.06]'
}

function DealRow({ row }: { row: GoldenReconRow }) {
  const rec = row.reconciliation
  const corresponds = rec.verdict === 'CORRESPONDS'
  const navigate = useNavigate()
  return (
    <>
      <tr
        className="border-b border-amber-400/[0.06] hover:bg-amber-400/[0.03] cursor-pointer"
        onClick={() => navigate(`/golden-deal-reconciliation/${row.model}`)}
        title="View the full run — extraction, recomputation, screen, verdict"
      >
        <td className="py-3 px-4 align-top">
          <div className="text-stone-100 font-medium flex items-center gap-1.5">
            {row.display}
            <ChevronRight size={13} className="text-amber-400/60" />
          </div>
          <div className="text-[11px] text-stone-500 mt-0.5 font-mono">{row.model} · view full run</div>
        </td>
        <td className="py-3 px-4 align-top">
          {row.fidelity_all_exact ? (
            <span className="inline-flex items-center gap-1 text-[11px] text-emerald-400 font-mono">
              <CheckCircle2 size={13} /> Exact
            </span>
          ) : (
            <span className="text-[11px] text-red-400 font-mono">Mismatch</span>
          )}
        </td>
        <td className="py-3 px-4 align-top">
          <span
            className={`inline-block text-[10px] font-mono uppercase tracking-[0.12em] px-2 py-0.5 rounded border ${screenBadge(
              row.screen_outcome,
            )}`}
          >
            {row.screen_outcome ?? '—'}
            {row.exceptions && row.exceptions.length > 0 && (
              <span className="normal-case tracking-normal"> · {row.exceptions.join(', ')}</span>
            )}
          </span>
        </td>
        <td className="py-3 px-4 align-top text-stone-200">{rec.actual}</td>
        <td className="py-3 px-4 align-top">
          <span
            className={`inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-[0.1em] ${
              corresponds ? 'text-emerald-400' : 'text-amber-400'
            }`}
          >
            {corresponds ? <CheckCircle2 size={13} /> : <AlertTriangle size={13} />}
            {rec.verdict}
          </span>
        </td>
      </tr>
      <tr className="border-b border-amber-400/[0.06]">
        <td colSpan={5} className="px-4 pb-3 pt-0">
          <div className="text-[12px] leading-relaxed text-stone-400 border-l-2 border-amber-400/20 pl-3">
            {rec.note}
          </div>
        </td>
      </tr>
    </>
  )
}

export default function GoldenDealReconciliation() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['golden-deal-reconciliation'],
    queryFn: () => reconciliationService.getMatrix(),
    staleTime: 60_000,
  })

  return (
    <div className="space-y-5 p-4">
      <div className="flex items-center gap-3">
        <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-amber-400/10 border border-amber-400/20">
          <Scale size={17} className="text-amber-300" />
        </div>
        <div>
          <h1 className="text-lg font-semibold text-stone-100">Golden-Deal Reconciliation</h1>
          <p className="text-xs text-stone-500">
            Your five golden deals, run through the platform against your own credit decisions.
          </p>
        </div>
      </div>

      {isLoading && (
        <div className="card p-6 text-[12px] text-stone-500 font-mono">Computing reconciliation…</div>
      )}
      {isError && (
        <div className="card p-6 text-[12px] text-red-400 font-mono">
          Failed to load the reconciliation.
        </div>
      )}

      {data && (
        <>
          <div className="card p-4">
            <div className="flex items-center justify-between mb-3">
              <Link to="/credit-box" className="terminal-kicker hover:text-amber-200 transition-colors" title="View the full ruleset">Credit Box v{data.rules_version} · M1 Calibration →</Link>
              <div className="flex items-center gap-1.5 text-[11px] text-emerald-400 font-mono">
                <ShieldCheck size={13} /> No-AI zone · deterministic
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="metric-cell">
                <div className="metric-cell-label">Coverage</div>
                <div className="metric-cell-value text-stone-100">{data.coverage}</div>
                <div className="text-[10px] text-stone-500 mt-0.5">models run end-to-end</div>
              </div>
              <div className="metric-cell">
                <div className="metric-cell-label">Correspondence</div>
                <div className="metric-cell-value text-emerald-400">{data.corresponds}</div>
                <div className="text-[10px] text-stone-500 mt-0.5">match your actual calls</div>
              </div>
              <div className="metric-cell">
                <div className="metric-cell-label">Model Fidelity</div>
                <div className="metric-cell-value text-emerald-400">
                  {data.all_exact_fidelity ? 'Exact' : 'Review'}
                </div>
                <div className="text-[10px] text-stone-500 mt-0.5">metric-for-metric</div>
              </div>
            </div>
          </div>

          <div className="card overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-amber-400/10">
                  {['Deal', 'Model Fidelity', 'Credit-Box Screen', 'Your Decision', 'Verdict'].map(
                    (h) => (
                      <th
                        key={h}
                        className="text-left py-2.5 px-4 text-[9px] font-mono uppercase tracking-[0.16em] text-stone-500"
                      >
                        {h}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody>
                {data.rows.map((row) => (
                  <DealRow key={row.model} row={row} />
                ))}
              </tbody>
            </table>
          </div>

          <p className="text-[11px] text-stone-500 leading-relaxed max-w-3xl">
            The screen never renders a decision — judgment factors route to human review by design.
            Correspondence means the platform reproduces each deal&apos;s metrics exactly and lands where
            your team landed: the deals you bid clear the box with judgment referred, and the one you
            passed on surfaces both decline drivers — a proceeds shortfall against the ask, and the
            tax-abatement judgment flagged for review.
          </p>
        </>
      )}
    </div>
  )
}
